import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext/AuthContext';
import { getVirtualWallet } from '../../services/wallet/wallet';
import { VirtualWalletDTO } from '../../services/wallet/interface';

interface WalletContextProps {
  wallet: VirtualWalletDTO | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const WalletContext = createContext<WalletContextProps | undefined>(undefined);

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tokens } = useAuth();
  const isAuthenticated = !!tokens;
  const [wallet, setWallet] = useState<VirtualWalletDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWallet = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getVirtualWallet();
      setWallet(data);
    } catch (e: any) {
      console.error('Failed to fetch wallet', e);
      setError(typeof e === 'string' ? e : 'Failed to fetch wallet');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWallet();
    } else {
      setWallet(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <WalletContext.Provider value={{ wallet, isLoading, error, refetch: fetchWallet }}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider.');
  }
  return context;
};
